import { LookupListEditor } from "@/components/LookupListEditor";
import { PolicyProductsEditor } from "@/components/PolicyProductsEditor";

// Reusable editor that wraps the existing dedicated `/api/kinship-degrees` endpoint.
// Provides the same UX as `LookupListEditor` but for the kinship_degrees collection.
import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronRight } from "lucide-react";

// 🆕 Sandwich panel: pannello collassabile per ogni sezione lista (default chiuso)
const CollapsiblePanel = ({ title, subtitle, emoji = "📋", defaultOpen = false, testid, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 mt-4 overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50 transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-3 text-left min-w-0">
          {open ? <ChevronDown className="h-4 w-4 text-slate-500 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-500 flex-shrink-0" />}
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2 truncate">{emoji} {title}</h2>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5 truncate">{subtitle}</p>}
          </div>
        </div>
        <span className="text-xs font-medium text-slate-400 flex-shrink-0">{open ? "—" : "+"}</span>
      </button>
      {open && <div className="px-5 pb-5 pt-1 border-t border-slate-100">{children}</div>}
    </div>
  );
};

const KinshipDegreesEditor = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", label: "", is_active: true, sort_order: 0 });
  const [editingId, setEditingId] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/kinship-degrees"); setItems(data || []); }
    catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    const code = (form.code || "").trim().toLowerCase().replace(/[^a-z0-9_-]/g, "_");
    if (!code) { toast.error("Codice obbligatorio"); return; }
    if (!form.label.trim()) { toast.error("Etichetta obbligatoria"); return; }
    const payload = { code, label: form.label.trim(), is_active: !!form.is_active, sort_order: Number(form.sort_order) || 0 };
    try {
      if (editingId) await api.put(`/kinship-degrees/${editingId}`, payload);
      else await api.post("/kinship-degrees", payload);
      toast.success(editingId ? "Aggiornato" : "Creato");
      setForm({ code: "", label: "", is_active: true, sort_order: 0 });
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 mt-6 p-5" data-testid="kinship-degrees-section">
      <div className="mb-3">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">👨‍👩‍👧 Gradi di parentela</h2>
        <p className="text-xs text-slate-500 mt-0.5">Lista valori del campo &quot;Grado di parentela&quot; nei preventivi Bersani.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-3 p-3 bg-slate-50 rounded-md border border-slate-200">
        <div className="sm:col-span-3"><Label className="text-xs">Codice</Label><Input data-testid="kin-form-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="es. coniuge" className="h-8 text-sm font-mono lowercase" disabled={!!editingId} /></div>
        <div className="sm:col-span-6"><Label className="text-xs">Etichetta</Label><Input data-testid="kin-form-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="es. Coniuge / Convivente" className="h-8 text-sm" /></div>
        <div className="sm:col-span-1"><Label className="text-xs">Ordine</Label><Input data-testid="kin-form-order" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="h-8 text-sm" /></div>
        <div className="sm:col-span-2 flex items-end gap-1">
          <Button type="button" size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={submit} data-testid="kin-form-submit">{editingId ? "Salva" : "+ Aggiungi"}</Button>
          {editingId && <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setEditingId(null); setForm({ code: "", label: "", is_active: true, sort_order: 0 }); }}>X</Button>}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-500 italic px-2 py-3">Nessun grado di parentela. I default verranno seedati alla prima apertura del campo nel preventivo.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="kin-items-table">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <th className="text-left px-3 py-2">Ordine</th><th className="text-left px-3 py-2">Codice</th><th className="text-left px-3 py-2">Etichetta</th><th className="text-center px-3 py-2">Attivo</th><th className="text-right px-3 py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`kin-row-${it.id}`}>
                  <td className="px-3 py-2 text-slate-500 tabular-nums">{it.sort_order ?? 0}</td>
                  <td className="px-3 py-2 font-mono text-xs">{it.code}</td>
                  <td className="px-3 py-2">{it.label}</td>
                  <td className="px-3 py-2 text-center">{it.is_active !== false ? <span className="text-emerald-600">✓</span> : <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => { setEditingId(it.id); setForm({ code: it.code, label: it.label, is_active: it.is_active !== false, sort_order: it.sort_order || 0 }); }} className="text-xs text-blue-700 hover:underline mr-3" data-testid={`kin-edit-${it.id}`}>Modifica</button>
                    <button
                      onClick={() => {
                        setEditingId(null);
                        const baseCode = `${it.code}_copy`;
                        let candidate = baseCode;
                        let n = 2;
                        const existing = new Set(items.map((x) => x.code));
                        while (existing.has(candidate)) candidate = `${baseCode}_${n++}`;
                        setForm({ code: candidate, label: `${it.label} (copia)`, is_active: it.is_active !== false, sort_order: (it.sort_order || 0) + 1 });
                        toast.info("Form precompilato. Clicca '+ Aggiungi' per salvare la copia.");
                      }}
                      className="text-xs text-emerald-700 hover:underline mr-3"
                      data-testid={`kin-duplicate-${it.id}`}
                    >Duplica</button>
                    <button onClick={async () => { if (!window.confirm(`Eliminare "${it.label}"?`)) return; try { await api.delete(`/kinship-degrees/${it.id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error(formatApiError(e)); } }} className="text-xs text-red-700 hover:underline" data-testid={`kin-delete-${it.id}`}>Elimina</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function LookupLists() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="lookup-lists-page">
      <div className="mb-4">
        <h1 className="text-2xl font-display font-semibold text-slate-900 flex items-center gap-2">📚 Liste preventivo</h1>
        <p className="text-sm text-slate-500 mt-1">
          Personalizza le voci dei campi a tendina usati nei preventivi, offerte e polizze (Stato civile, Sesso, Tipo guida, Frazionamento, Professione, Alimentazione aggiuntiva, Grado di parentela).
        </p>
      </div>

      <CollapsiblePanel
        emoji="👨‍👩‍👧"
        title="Gradi di parentela"
        subtitle="Lista valori del campo 'Grado di parentela' nei preventivi Bersani."
        testid="panel-kinship"
      >
        <KinshipDegreesEditor />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="🛡️"
        title="Prodotti / Garanzie per tipologia"
        subtitle="Garanzie disponibili per ogni tipologia di polizza."
        testid="panel-policy-products"
      >
        <PolicyProductsEditor />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="💍"
        title="Stato civile"
        subtitle="Valori del campo 'Stato civile' nei preventivi/clienti."
        testid="panel-marital-status"
      >
        <LookupListEditor
          listKey="marital_status"
          emoji="💍"
          title="Stato civile"
          description="Valori del campo 'Stato civile' nei preventivi/clienti."
          codePlaceholder="es. coniugato"
          labelPlaceholder="es. Coniugato/a"
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="⚧"
        title="Sesso"
        subtitle="Valori del campo 'Sesso' nei preventivi e nelle anagrafiche."
        testid="panel-gender"
      >
        <LookupListEditor
          listKey="gender"
          emoji="⚧"
          title="Sesso"
          description="Valori del campo 'Sesso' nei preventivi e nelle anagrafiche."
          codePlaceholder="es. M"
          labelPlaceholder="es. Maschio"
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="🪪"
        title="Tipo di guida"
        subtitle="Valori del campo 'Tipo di guida' nelle offerte e nei preventivi."
        testid="panel-driving-type"
      >
        <LookupListEditor
          listKey="driving_type"
          emoji="🪪"
          title="Tipo di guida"
          description="Valori del campo 'Tipo di guida' nelle offerte e nei preventivi."
          codePlaceholder="es. esclusiva"
          labelPlaceholder="es. Esclusiva"
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="📅"
        title="Frazionamento pagamento"
        subtitle="Periodicità rate (annuale, semestrale, mensile…). Visibili in preventivi, offerte e polizze."
        testid="panel-frazionamento"
      >
        <LookupListEditor
          listKey="frazionamento"
          emoji="📅"
          title="Frazionamento pagamento"
          description="Periodicità rate (annuale, semestrale, mensile…). Modifiche visibili in preventivi, offerte e polizze."
          codePlaceholder="es. annuale"
          labelPlaceholder="es. Annuale (1 rata/anno)"
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="💼"
        title="Professione"
        subtitle="Valori del campo 'Professione' del contraente nei preventivi/polizze."
        testid="panel-profession"
      >
        <LookupListEditor
          listKey="profession"
          emoji="💼"
          title="Professione"
          description="Valori del campo 'Professione' nei preventivi e nelle polizze."
          codePlaceholder="es. impiegato"
          labelPlaceholder="es. Impiegato/a"
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="⛽"
        title="Alimentazione"
        subtitle="Alimentazioni principali del veicolo (benzina, diesel, GPL, elettrico, ibrido, ecc.)."
        testid="panel-fuel"
      >
        <LookupListEditor
          listKey="fuel"
          emoji="⛽"
          title="Alimentazione"
          description="Voci del campo 'Alimentazione' del veicolo nel Wizard polizza e nei preventivi."
          codePlaceholder="es. benzina"
          labelPlaceholder="es. Benzina"
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="⛽"
        title="Alimentazione aggiuntiva"
        subtitle="Alimentazioni secondarie del veicolo (GPL, metano, elettrico ibrido, ecc.)."
        testid="panel-additional-fuel"
      >
        <LookupListEditor
          listKey="additional_fuel"
          emoji="⛽"
          title="Alimentazione aggiuntiva"
          description="Alimentazioni secondarie del veicolo (GPL, metano, elettrico ibrido, ecc.)."
          codePlaceholder="es. gpl"
          labelPlaceholder="es. GPL"
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="🛡️"
        title="Garanzie / Coperture"
        subtitle="Elenco delle garanzie selezionabili nei preventivi e nelle polizze (RCA, Furto, Kasko, ecc.)."
        testid="panel-coverages"
      >
        <LookupListEditor
          listKey="coverages"
          emoji="🛡️"
          title="Garanzie / Coperture"
          description="Elenco delle garanzie selezionabili nei preventivi/polizze. Aggiungi qui le voci che usi più spesso."
          codePlaceholder="es. rca"
          labelPlaceholder="es. RCA - Responsabilità Civile Auto"
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="🛣️"
        title="Km annui"
        subtitle="Fasce di percorrenza annua (usato nella richiesta preventivo pubblica)."
        testid="panel-annual-mileage"
      >
        <LookupListEditor
          listKey="annual_mileage"
          emoji="🛣️"
          title="Km annui"
          description="Fasce di percorrenza annua stimata (es. Fino a 5.000 km, 5.000-10.000 km, ecc.)."
          codePlaceholder="es. 10000_15000"
          labelPlaceholder="es. 10.000 – 15.000 km"
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="🔒"
        title="Antifurto"
        subtitle="Sistemi antifurto installati sul veicolo (immobilizer, allarme, satellitare…)."
        testid="panel-antitheft"
      >
        <LookupListEditor
          listKey="antitheft"
          emoji="🔒"
          title="Antifurto"
          description="Elenco dei sistemi antifurto disponibili nel form preventivo."
          codePlaceholder="es. satellitare"
          labelPlaceholder="es. Satellitare (GPS)"
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="🎓"
        title="Titolo di studio"
        subtitle="Livelli di istruzione del conducente (media, diploma, laurea…)."
        testid="panel-education"
      >
        <LookupListEditor
          listKey="education"
          emoji="🎓"
          title="Titolo di studio"
          description="Livelli di istruzione del conducente utilizzati nel form preventivo."
          codePlaceholder="es. diploma"
          labelPlaceholder="es. Diploma di scuola superiore"
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="🏢"
        title="Compagnia attuale"
        subtitle="Elenco delle compagnie assicurative usato nel campo 'Compagnia attuale' del cliente."
        testid="panel-current-company"
      >
        <LookupListEditor
          listKey="current_company"
          emoji="🏢"
          title="Compagnia attuale"
          description="Compagnie assicurative disponibili nel dropdown 'Compagnia attuale' del preventivo."
          codePlaceholder="es. generali"
          labelPlaceholder="es. Generali"
        />
      </CollapsiblePanel>

      <CollapsiblePanel
        emoji="🚗"
        title="Situazione assicurativa"
        subtitle="Stato del veicolo del cliente (già assicurato / nuovo da assicurare)."
        testid="panel-insurance-status"
      >
        <LookupListEditor
          listKey="insurance_status"
          emoji="🚗"
          title="Situazione assicurativa"
          description="Stato del veicolo (es. Veicolo già assicurato, Nuovo veicolo da assicurare)."
          codePlaceholder="es. already_insured"
          labelPlaceholder="es. Veicolo già assicurato"
        />
      </CollapsiblePanel>

      {/* 🆕 Attestato di rischio altro veicolo (Bersani source) */}
      <CollapsiblePanel
        emoji="📜"
        title="Attestato di rischio altro veicolo"
        subtitle="Voci del campo &quot;Vuoi utilizzare l'attestato di rischio di un altro veicolo?&quot; nel wizard preventivo. Ogni voce può aprire la sezione Bersani/RC Familiare (familiare convivente) oppure chiedere la targa + dati del veicolo di origine."
        testid="panel-bersani-source"
      >
        <LookupListEditor
          listKey="bersani_source"
          emoji="📜"
          title="Attestato altro veicolo"
          description="Es. No / Sì, Bersani stesso proprietario / Sì, Bersani familiare convivente / Sì, veicolo venduto, rubato o rottamato."
          codePlaceholder="es. bersani_same_owner"
          labelPlaceholder="es. Sì, Bersani stesso proprietario"
        />
      </CollapsiblePanel>

      {/* 🆕 Stati sinistro */}
      <CollapsiblePanel
        emoji="⚠️"
        title="Stati Sinistro"
        subtitle="Stati disponibili per i sinistri (aperto, denunciato, in istruttoria, chiuso…). Modificabili per agenzia."
        testid="panel-claim-status"
      >
        <LookupListEditor
          listKey="claim_status"
          emoji="⚠️"
          title="Stati Sinistro"
          description="Elenco stati del sinistro visualizzati nel dropdown della scheda sinistro."
          codePlaceholder="es. in_istruttoria"
          labelPlaceholder="es. In istruttoria"
        />
      </CollapsiblePanel>

      {/* 🆕 Template invito cliente per denuncia sinistro */}
      <CollapsiblePanel
        emoji="✉️"
        title="Template invito cliente (denuncia sinistro)"
        subtitle="Messaggi predefiniti inviati al cliente per invitarlo a presentare la denuncia sinistro in agenzia (via email o WhatsApp)."
        testid="panel-claim-invite-templates"
      >
        <div className="text-xs text-slate-600 bg-blue-50/60 border border-blue-200 rounded-md p-3 mb-3">
          <p className="font-semibold mb-1">📝 Placeholder disponibili nel testo</p>
          <p><code className="bg-white border border-slate-200 px-1 rounded">{"{cliente}"}</code>, <code className="bg-white border border-slate-200 px-1 rounded">{"{agenzia}"}</code>, <code className="bg-white border border-slate-200 px-1 rounded">{"{telefono_agenzia}"}</code>, <code className="bg-white border border-slate-200 px-1 rounded">{"{indirizzo_agenzia}"}</code>, <code className="bg-white border border-slate-200 px-1 rounded">{"{polizza}"}</code>, <code className="bg-white border border-slate-200 px-1 rounded">{"{compagnia}"}</code>, <code className="bg-white border border-slate-200 px-1 rounded">{"{data_sinistro}"}</code></p>
          <p className="mt-1 italic">I placeholder vengono sostituiti automaticamente al momento dell&apos;invio.</p>
        </div>
        <LookupListEditor
          listKey="claim_invite_templates"
          emoji="✉️"
          title="Template invito"
          description="Testi personalizzabili per l'invito cliente in agenzia."
          codePlaceholder="es. sollecito_urgente"
          labelPlaceholder="Es. Gentile {cliente}, ti invitiamo a passare in agenzia {agenzia}…"
          labelMultiline
          labelRows={5}
        />
      </CollapsiblePanel>
    </div>
  );
}
